import { addContentGroupMembers } from "@/sideEffects/api";
import { selectActiveFirm } from "@/store/appSelectors";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ContentCrud } from "../content/ContentCrud";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { showApiError, showSuccess } from "@/lib/toast";
import { LoadingOverlay } from "@/components/LoadingOverlay";
import { ContentGroup } from "@/lib/types";

interface Props {
  contentGroup: ContentGroup;
  onOpenChange: (open: boolean) => void;
}

export const AddContentGroupMemberDialog: React.FC<Props> = ({ contentGroup, onOpenChange }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);

  const queryClient = useQueryClient();
  const addContentGroupMemberMutation = useMutation({
    mutationFn: (values: { contentGuid: string; duration?: number }) =>
      addContentGroupMembers(
        String(activeFirm?.firmGuid),
        String(contentGroup.contentGroupGuid),
        values.contentGuid,
        values.duration
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["contentGroupMembers"],
      });
      showSuccess(t("common.assignedSuccess"));
    },
    onError: showApiError,
  });

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[90vw] h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("contentGroup.assignContent")}</DialogTitle>
          <DialogDescription>
            <LoadingOverlay isLoading={addContentGroupMemberMutation.isPending}>
              <ContentCrud
                selectConfig={{
                  types: ["image", "video", "audio", "fromTemplate", "split", "link", "doc"],
                  onSelect: ({ contentGuid, contentDuration }) => {
                    addContentGroupMemberMutation.mutate({ contentGuid, duration: contentDuration });
                  },
                  defaultScreenSizeGuid: contentGroup.screenSizeId,
                }}
              />
            </LoadingOverlay>
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};
